import {
  Description,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Dialog as HeadlessDialog,
} from '@headlessui/react'
import { clsx } from 'clsx'
import { X } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { useDragDismiss } from '@/hooks/useDragDismiss'

// Tailwind `sm` 브레이크포인트(640px) 미만일 때만 바텀시트 레이아웃이 활성화된다.
function useIsBottomSheet(): boolean {
  const [isSheet, setIsSheet] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia('(max-width: 639.98px)')
    const update = () => setIsSheet(mql.matches)
    update()
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [])
  return isSheet
}

interface DialogProps {
  open: boolean
  onClose: () => void
  children: ReactNode
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl'
  noPadding?: boolean
}

const sizeStyles = {
  xs: 'sm:max-w-xs',
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
}

export function Dialog({ open, onClose, children, size = 'lg', noPadding = false }: DialogProps) {
  const isSheet = useIsBottomSheet()
  // 바텀시트(모바일)에서만 드래그-투-디스미스 활성화. 데스크톱 중앙 모달은 비활성.
  const { sheetRef, dragHandlers } = useDragDismiss({
    onClose,
    disabled: !isSheet,
  })

  return (
    <HeadlessDialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className={clsx(
          'dialog-backdrop',
          'transition data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in'
        )}
      />

      <div className="fixed inset-0 overflow-y-auto pt-6 sm:pt-0">
        <div className="grid min-h-full grid-rows-[1fr_auto] justify-items-center sm:grid-rows-[1fr_auto_3fr] sm:p-4">
          <DialogPanel
            ref={sheetRef}
            transition
            className={clsx(
              'dialog-panel row-start-2 w-full min-w-0 rounded-t-2xl',
              noPadding
                ? 'pb-[env(safe-area-inset-bottom,0px)]'
                : 'p-6 pb-[max(1.5rem,env(safe-area-inset-bottom,0px))] sm:p-8 sm:pb-8',
              'sm:mb-auto sm:rounded-xl',
              sizeStyles[size],
              'transition duration-300 data-[closed]:translate-y-12 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in',
              'sm:data-[closed]:translate-y-0 sm:data-[closed]:scale-95 sm:data-[closed]:data-[enter]:duration-300 sm:data-[closed]:data-[leave]:duration-200'
            )}
          >
            {/* Mobile drag handle indicator — drag-to-dismiss */}
            <div className="dialog-handle sm:hidden" {...dragHandlers}>
              <div className="dialog-handle__bar" />
            </div>
            {children}
          </DialogPanel>
        </div>
      </div>
    </HeadlessDialog>
  )
}

interface DialogHeaderProps {
  title: string
  description?: string
  onClose?: () => void
}

export function DialogHeader({ title, description, onClose }: DialogHeaderProps) {
  return (
    <div className="relative">
      <DialogTitle className="text-balance text-lg/6 font-semibold text-heading sm:text-base/6">
        {title}
      </DialogTitle>
      {description && (
        <Description className="mt-2 text-pretty text-sm/6 text-caption">
          {description}
        </Description>
      )}
      {onClose && (
        <div className="absolute right-0 top-0">
          <button
            onClick={onClose}
            className="dialog-close"
            aria-label="닫기"
          >
            <X className="dialog-close__icon" />
          </button>
        </div>
      )}
    </div>
  )
}

interface DialogBodyProps {
  children: ReactNode
  className?: string
}

export function DialogBody({ children, className }: DialogBodyProps) {
  return <div className={clsx('mt-6', className)}>{children}</div>
}

interface DialogFooterProps {
  children: ReactNode
  className?: string
}

export function DialogFooter({ children, className }: DialogFooterProps) {
  return (
    <div
      className={clsx(
        'mt-6 flex flex-col-reverse items-center justify-end gap-3 *:w-full sm:flex-row sm:*:w-auto',
        className
      )}
    >
      {children}
    </div>
  )
}
